<template>
  <div class="brand-result-page">
    <!-- 头部导航 -->
    <div class="page-header">
      <div class="header-content">
        <div class="logo" @click="goHome">
          <img :src="logoUrl" alt="中品指数" />
          <span class="logo-text">中品指数</span>
        </div>
        <nav class="navbar">
          <a href="/brand-index" class="nav-link">首页</a>
          <a href="#" class="nav-link active">资讯指数</a>
        </nav>

        <!-- 顶部搜索框 -->
        <div class="header-search">
          <input
            type="text"
            v-model="headerKeyword"
            placeholder="请输入您想查询的关键词"
            @keyup.enter="searchFromHeader"
          />
          <button class="header-search-btn" @click="searchFromHeader">
            <img :src="searchIconUrl" alt="搜索" />
          </button>
        </div>

        <!-- 已登录：显示头像，点击跳转个人中心 -->
        <img
          v-if="userInfo.userCode"
          class="user-avatar"
          referrerpolicy="no-referrer"
          :src="userInfo.avatar || defaultAvatar"
          alt="个人中心"
          @click="goUserCenter"
        />

        <!-- 未登录：显示登录 -->
        <a v-else href="javascript:;" class="nav-link login-link" @click="goLogin">登录</a>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-container">
      <div class="content-wrapper">
        <!-- 搜索区域 -->
        <div class="search-section">
          <div class="keyword-tabs">
            <div class="tab-group active">关键词</div>

            <div class="keywords-list">
              <div
                v-for="(kw) in keywords"
                :key="kw.text"
                class="keyword-item"
                :class="{ removed: kw.removed }"
              >
                <span class="keyword-text">{{ kw.text }}</span>
                <button
                  v-if="kw.removable"
                  class="remove-btn"
                  @click="removeKeyword(kw)"
                >×</button>
              </div>

              <button v-if="tableCount < 4" class="add-btn" @click="showAddModal = true">+ 添加对比</button>
            </div>
          </div>
        </div>

        <!-- 图表区域 -->
        <div class="chart-section">
          <!-- 图表头部：标题 + 参数 -->
          <div class="chart-header">
            <div class="chart-title-tabs">
              <div class="chart-title-tab active">资讯指数</div>
            </div>

            <div class="search-params">
              <div class="param-item">
                <label>对比时间段</label>
              </div>
              <div class="param-sep">|</div>

              <div class="param-item">
                <span class="param-value">{{ dateRangeText }}</span>
              </div>
              <div class="param-sep">|</div>

              <!-- 快捷时间选择 -->
              <div class="param-item dropdown" @click.stop="toggleDateMenu">
                <label>{{ activeDateLabel }}</label>
                <span class="arrow">▼</span>

                <!-- 下拉菜单 -->
                <ul v-if="showDateMenu" class="dropdown-menu" @click.stop>
                  <li
                    v-for="opt in dateOptions"
                    :key="opt.value"
                    :class="{ selected: opt.value === activeDateKey }"
                    @click.stop="selectDateOption(opt)"
                  >{{ opt.label }}</li>
                  <li
                    class="custom-option"
                    :class="{ selected: activeDateKey === 'custom' }"
                    @click.stop="openCustomPicker"
                  >自定义时间</li>
                </ul>

                <!-- 自定义日期选择面板 -->
                <div
                  v-if="showCustomPicker"
                  class="custom-date-panel"
                  @click.stop
                >
                  <div class="panel-header">自定义时间</div>
                  <div class="panel-body">
                    <div class="date-row">
                      <span class="row-label">起始</span>
                      <input
                        type="date"
                        v-model="customStart"
                        :min="minDate"
                        :max="maxDate"
                        class="date-input"
                      />
                    </div>
                    <div class="date-row">
                      <span class="row-label">结束</span>
                      <input
                        type="date"
                        v-model="customEnd"
                        :min="customStart || minDate"
                        :max="maxDate"
                        class="date-input"
                      />
                    </div>
                  </div>
                  <div class="panel-footer">
                    <button class="panel-btn confirm" @click="confirmCustomDate">确定</button>
                    <button class="panel-btn cancel" @click="showCustomPicker = false">取消</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 图例 -->
          <div class="chart-legend">
            <div
              v-for="kw in displayKeywords"
              :key="kw.text"
              class="legend-item"
            >
              <span class="legend-color" :style="{ backgroundColor: kw.color }"></span>
              <span class="legend-text">{{ kw.text }}</span>
            </div>
          </div>

          <!-- 图表容器 -->
          <div class="chart-container" ref="chartContainer">
            <div v-if="loading" class="chart-loading">
              <div class="loading-spinner"></div>
              <span>加载中...</span>
            </div>
            <div v-if="error" class="chart-error">{{ error }}</div>
            <div
              v-if="!loading && !error && displayKeywords.length === 0"
              class="chart-empty"
            >请输入关键词进行搜索</div>
            <div
              v-if="!loading && !error && displayKeywords.length > 0 && !hasAnyData"
              class="chart-no-data"
            >暂无数据</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 添加关键词弹窗 -->
    <div v-if="showAddModal" class="modal-mask" @click.self="showAddModal = false">
      <div class="modal-dialog">
        <div class="modal-header">
          <h3>添加对比关键词</h3>
        </div>
        <div class="modal-body">
          <input
            type="text"
            v-model="newKeyword"
            placeholder="请输入要对比的关键词"
            @keyup.enter="confirmAddKeyword"
            ref="newKeywordInput"
          />
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="showAddModal = false">取消</button>
          <button class="btn-confirm" @click="confirmAddKeyword">确定</button>
        </div>
      </div>
    </div>

    <!-- 登录弹窗 -->
    <div v-if="showLoginDialog" class="login-modal-mask">
      <div class="login-modal-container">
        <LoginCard
          :visible="true"
          :modal-mode="true"
          @close="closeLoginDialog"
          @login-success="handleLoginSuccess"
        />
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '../../api/request'
import LoginCard from '../login/LoginCard.vue'

export default {
  name: 'BrandResult',
  components: {
    LoginCard
  },
  data() {
    return {
      logoUrl: 'https://image.cbrand.com.cn/default/2026/10/3AAB497BA8984F21A4A68ECE1F55EBF0.png',
      searchIconUrl: 'https://image.cbrand.com.cn/default/2026/10/7E50AAC3620F4CA68C1DB760A491B738.png',

      // 关键词列表（每项结构：{ text, removable, color, data: [], removed: false }）
      keywords: [],
      headerKeyword: '',

      showAddModal: false,
      newKeyword: '',

      loading: false,
      error: '',
      chartInstance: null,
      resizeHandler: null,

      minDate: '2017-01-01',
      maxDate: '',
      dateStart: '',
      dateEnd: '',

      showDateMenu: false,
      activeDateKey: '30',
      dateOptions: [
        { label: '近 7 天', value: '7' },
        { label: '近 30 天', value: '30' },
        { label: '近 90 天', value: '90' },
        { label: '近半年', value: '180' },
        { label: '近一年', value: '365' },
        { label: '全部', value: 'all' }
      ],

      showCustomPicker: false,
      customStart: '',
      customEnd: '',

      // 颜色池
      colorPool: ['#1ba6e2', '#ff7c3f', '#924aff', '#ff1a1a', '#00cba9'],

      // ===== 用户 / 登录相关 =====
      defaultAvatar: 'https://image.cbrand.com.cn/tmp/a35f6d318d63902f817176097c6ca690.png',
      userInfo: {
        nickname: '',
        avatar: '',
        userCode: ''
      },
      showLoginDialog: false,
      tableCount: 1
    }
  },
  computed: {
    displayKeywords() {
      return this.keywords.filter(k => !k.removed)
    },
    dateRangeText() {
      if (!this.dateStart || !this.dateEnd) return '-'
      return `${this.dateStart} ~ ${this.dateEnd}`
    },
    activeDateLabel() {
      if (this.activeDateKey === 'custom') return '自定义时间'
      const opt = this.dateOptions.find(o => o.value === this.activeDateKey)
      return opt ? opt.label : '近 30 天'
    },
    // 是否至少有一个关键词有数据
    hasAnyData() {
      return this.displayKeywords.some(kw => kw.data && kw.data.length > 0)
    }
  },
  watch: {
    '$route.query.keyword': {
      handler(newKeyword) {
        if (!newKeyword) return
        const firstKw = this.keywords.find(k => !k.removable)
        if (firstKw && firstKw.text === newKeyword) return
        this.initFromQuery(newKeyword)
      }
    }
  },
  mounted() {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    this.maxDate = this.formatDate(today)

    this.applyDateRange('30')

    const queryKeyword = this.$route.query.keyword
    if (queryKeyword) {
      this.initFromQuery(queryKeyword)
    }

    document.addEventListener('click', this.closeDateMenu)

    // 加载用户信息 + 监听刷新事件
    this.fetchUserInfo()
    window.addEventListener('refresh-user-info', this.handleRefreshUserInfo)
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
    document.removeEventListener('click', this.closeDateMenu)
    window.removeEventListener('refresh-user-info', this.handleRefreshUserInfo)
  },
  methods: {
    formatDate(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },

    // 根据 URL 关键词初始化页面
    initFromQuery(keyword) {
      if (!keyword) return
      this.keywords = [{
        text: keyword,
        removable: false,
        color: this.colorPool[0],
        data: [],
        removed: false
      }]
      this.headerKeyword = keyword
      this.error = ''
      this.$nextTick(() => {
        this.fetchData()
      })
    },

    goHome() {
      this.$router.push('/')
    },

    // 顶部搜索：重新跳转
    searchFromHeader() {
      const text = this.headerKeyword.trim()
      if (!text) return
      const firstKw = this.keywords.find(k => !k.removable)
      if (firstKw && firstKw.text === text) {
        // 同关键词直接刷新
        this.fetchData()
        return
      }
      this.$router.push({
        path: '/brand-result',
        query: { keyword: text }
      })
    },

    // 快捷日期
    toggleDateMenu() {
      this.showDateMenu = !this.showDateMenu
      if (!this.showDateMenu) this.showCustomPicker = false
    },
    closeDateMenu() {
      this.showDateMenu = false
      this.showCustomPicker = false
    },
    selectDateOption(opt) {
      this.activeDateKey = opt.value
      this.showDateMenu = false
      this.showCustomPicker = false
      this.applyDateRange(opt.value)
      if (this.displayKeywords.length > 0) this.handleSearch()
    },
    applyDateRange(days) {
      if (days === 'all') {
        this.dateStart = this.minDate
        this.dateEnd = this.maxDate
        return
      }
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const end = new Date(today)
      const start = new Date(today)
      start.setDate(start.getDate() - (Number(days) - 1))
      this.dateEnd = this.formatDate(end)
      this.dateStart = this.formatDate(start)
    },
    openCustomPicker() {
      this.customStart = this.dateStart || this.minDate
      this.customEnd = this.dateEnd || this.maxDate
      this.showCustomPicker = true
    },
    confirmCustomDate() {
      if (!this.customStart || !this.customEnd) {
        alert('请选择完整的日期范围')
        return
      }
      if (this.customStart > this.customEnd) {
        alert('起始日期不能大于结束日期')
        return
      }
      this.dateStart = this.customStart
      this.dateEnd = this.customEnd
      this.activeDateKey = 'custom'
      this.showCustomPicker = false
      this.showDateMenu = false
      if (this.displayKeywords.length > 0) this.handleSearch()
    },

    // 删除关键词
    removeKeyword(kw) {
      const idx = this.keywords.indexOf(kw)
      if (idx === -1) return
      this.tableCount--
      this.$set(this.keywords[idx], 'removed', true)

      setTimeout(() => {
        const realIdx = this.keywords.indexOf(kw)
        if (realIdx !== -1) {
          this.keywords.splice(realIdx, 1)
        }

        if (this.displayKeywords.length === 0) {
          if (this.chartInstance) this.chartInstance.clear()
          return
        }
        const remainData = this.displayKeywords.map(k => k.data || [])
        this.$nextTick(() => {
          this.renderChart(remainData)
        })
      }, 300)
    },

    // 弹窗确认添加
    confirmAddKeyword() {
      if (!this.newKeyword.trim()) return
      const text = this.newKeyword.trim()
      if (this.keywords.some(k => k.text === text && !k.removed)) {
        alert('该关键词已存在')
        return
      }
      const usedColors = this.keywords.map(k => k.color)
      const availableColors = this.colorPool.filter(c => !usedColors.includes(c))
      const color = availableColors.length > 0
        ? availableColors[0]
        : this.colorPool[this.keywords.length % this.colorPool.length]

      this.keywords.push({
        text,
        removable: true,
        color,
        data: [],
        removed: false
      })
      this.newKeyword = ''
       this.tableCount++
      this.showAddModal = false
      this.handleSearch()
     
    },

    handleSearch() {
      if (this.displayKeywords.length === 0) return
      this.fetchData()
    },

    async fetchData() {
      if (this.displayKeywords.length === 0) {
        this.error = '请输入至少一个关键词'
        return
      }

      this.loading = true
      this.error = ''

      const targets = this.displayKeywords.slice()

      try {
        const requests = targets.map(kw =>
          request.post('/api/news/brandInfluenceIndex', {
            query: kw.text,
            publishDateStart: this.dateStart,
            publishDateEnd: this.dateEnd
          })
        )

        const responses = await Promise.all(requests)

        targets.forEach((kw, i) => {
          const data = responses[i]?.data || []
          kw.data = data
        })

        const renderData = this.displayKeywords.map(k => k.data || [])
        this.$nextTick(() => {
          this.renderChart(renderData)
        })
      } catch (err) {
        console.error('查询失败:', err)
        this.error = err.message || '网络错误或服务器异常'
      } finally {
        this.loading = false
      }
    },

    initChart() {
      const container = this.$refs.chartContainer
      if (!container) return
      if (this.chartInstance) this.chartInstance.dispose()
      this.chartInstance = echarts.init(container)
      this.resizeHandler = () => {
        if (this.chartInstance) this.chartInstance.resize()
      }
      window.addEventListener('resize', this.resizeHandler)
    },

    renderChart(allData) {
      if (!this.chartInstance) this.initChart()

      if (!allData || allData.length === 0) {
        this.chartInstance.clear()
        return
      }

      const kws = this.displayKeywords
      const dates = allData[0]?.map(item => item.date) || []

      const series = kws.map((kw, idx) => {
        const data = allData[idx] || []
        const color = kw.color || '#1ba6e2'
        return {
          name: kw.text,
          type: 'line',
          data: data.map(item => item.count),
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: { color },
          lineStyle: { width: 2, color },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: color + '40' },
              { offset: 1, color: color + '05' }
            ])
          }
        }
      })

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'line',
            lineStyle: { color: '#999', type: 'dashed' }
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '5%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dates,
          boundaryGap: false,
          axisLabel: { interval: 'auto', rotate: 30 }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { type: 'dashed' } }
        },
        series
      }

      this.chartInstance.setOption(option, true)
    },

    // ========== 登录 / 用户信息相关 ==========
    goLogin() {
      console.log('[BrandResult] goLogin called, opening login dialog')
      this.showLoginDialog = true
    },
    closeLoginDialog() {
      console.log('[BrandResult] closing login dialog')
      this.showLoginDialog = false
    },
    handleLoginSuccess() {
      console.log('[BrandResult] login success, refreshing user info')
      this.closeLoginDialog()
      window.dispatchEvent(new CustomEvent('refresh-user-info'))
      this.fetchUserInfo()
    },
    handleRefreshUserInfo() {
      console.log('[BrandResult] refresh-user-info event received')
      this.fetchUserInfo()
    },
    async fetchUserInfo() {
      const token = localStorage.getItem('token')
      console.log('[BrandResult] fetchUserInfo called, token exists:', !!token)
      if (!token) {
        this.userInfo = { nickname: '', avatar: '', userCode: '' }
        return
      }
      try {
        const userType = localStorage.getItem('userType') || 'personal'
        let data = {}
        if (userType === 'company') {
          const res = await request.post('/api/company/home/getCompanyInfo')
          data = res.data || {}
          this.userInfo = {
            nickname: data.companyShortName || '',
            avatar: data.avatar || '',
            userCode: data.companyCode || ''
          }
        } else {
          const res = await request.post('/api/member/home/getUserInfo')
          data = res.data || {}
          this.userInfo = {
            nickname: data.nickname || '',
            avatar: data.avatar || '',
            userCode: data.userCode || ''
          }
        }
        console.log('[BrandResult] User info loaded:', this.userInfo)
      } catch (e) {
        console.error('[BrandResult] Failed to fetch user info:', e)
        localStorage.removeItem('token')
        this.userInfo = { nickname: '', avatar: '', userCode: '' }
      }
    },
    goUserCenter() {
      this.$router.push('/my')
    }
  }
}
</script>

<style scoped>
.brand-result-page {
  min-height: 100vh;
  background: #f5f7fa;
}

/* 头部导航 */
.page-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  z-index: 1000;
}

.header-content {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 20px;
  box-sizing: border-box;
  gap: 40px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  flex-shrink: 0;
}

.logo img {
  height: 40px;
  width: auto;
}

.logo-text {
  font-size: 20px;
  font-weight: bold;
  color: #C00513;
}

.navbar {
  display: flex;
  gap: 40px;
  flex-shrink: 0;
}

.nav-link {
  font-size: 15px;
  color: #333;
  text-decoration: none;
  transition: color 0.3s;
  position: relative;
  padding-bottom: 4px;
  white-space: nowrap;
  cursor: pointer;
}

.nav-link:hover {
  color: #1ba6e2;
}

.nav-link.active {
  color: #1ba6e2;
  font-weight: 500;
}

.nav-link.active::after {
  content: '';
  position: absolute;
  bottom: -6px;
  left: 0;
  width: 100%;
  height: 2px;
  background: #1ba6e2;
}

/* 顶部搜索框 */
.header-search {
  flex: 1;
  display: flex;
  align-items: center;
  max-width: 400px;
  margin-left: auto;
  border: 1px solid #d9e2f5;
  border-radius: 20px;
  overflow: hidden;
  height: 38px;
  background: #fff;
}

.header-search input {
  flex: 1;
  height: 100%;
  border: none;
  outline: none;
  padding: 0 16px;
  font-size: 14px;
  background: transparent;
  color: #333;
}

.header-search input::placeholder {
  color: #aaa;
}

.header-search-btn {
  width: 44px;
  height: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.header-search-btn img {
  width: 20px;
  height: 20px;
}

.header-search-btn:hover {
  background: #f0f9fd;
}

.login-link {
  color: #333 !important;
  font-weight: 600 !important;
  flex-shrink: 0;
}

/* 已登录头像 */
.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  cursor: pointer;
  border: 1px solid #eee;
  transition: box-shadow 0.2s, transform 0.2s;
  flex-shrink: 0;
}
.user-avatar:hover {
  box-shadow: 0 2px 12px rgba(27, 166, 226, 0.35);
  transform: translateY(-1px);
}

/* 主内容区 */
.main-container {
  width: 1400px;
  max-width: 100%;
  margin: 64px auto 0;
  padding: 20px;
  box-sizing: border-box;
}

.content-wrapper {
  background: #fff;
  border-radius: 8px;
  padding: 24px 30px 30px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

/* 搜索区域 */
.search-section {
  margin-bottom: 20px;
}

.keyword-tabs {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.tab-group {
  padding: 8px 24px;
  background: #1ba6e2;
  color: #fff;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
}

.keywords-list {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1;
}

.keyword-item {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-left: 3px solid #1ba6e2;
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.3s;
}

.keyword-item.removed {
  opacity: 0;
  transform: scale(0.9);
}

.keyword-text {
  color: #333;
}

.remove-btn {
  margin-left: 8px;
  width: 18px;
  height: 18px;
  border: none;
  background: #ccc;
  color: #fff;
  border-radius: 50%;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.remove-btn:hover {
  background: #ff4d4f;
}

.add-btn {
  padding: 6px 14px;
  background: #fff;
  color: #1ba6e2;
  border: 1px dashed #1ba6e2;
  border-radius: 16px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.add-btn:hover {
  background: #f0f9fd;
}

.confirm-btn {
  padding: 8px 28px;
  background: #fff;
  color: #1ba6e2;
  border: 1px solid #1ba6e2;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
  flex-shrink: 0;
}

.confirm-btn:hover {
  background: #1ba6e2;
  color: #fff;
}

/* 图表区域 */
.chart-section {
  margin-top: 20px;
}

.chart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 12px;
  margin-bottom: 16px;
}

.chart-title-tabs {
  display: flex;
  gap: 24px;
}

.chart-title-tab {
  font-size: 16px;
  color: #666;
  cursor: pointer;
  padding-bottom: 12px;
  margin-bottom: -13px;
  position: relative;
}

.chart-title-tab.active {
  color: #1ba6e2;
  font-weight: 600;
}

.chart-title-tab.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: #1ba6e2;
  border-radius: 2px;
}

.search-params {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: #666;
}

.param-item {
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
}

.param-item.dropdown {
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  user-select: none;
}

.param-item.dropdown:hover {
  background: #f5f5f5;
}

.param-item .arrow {
  font-size: 10px;
  color: #999;
  margin-left: 2px;
}

/* 下拉菜单 */
.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  list-style: none;
  padding: 4px 0;
  min-width: 120px;
  z-index: 100;
}

.dropdown-menu li {
  padding: 8px 16px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.dropdown-menu li:hover {
  background: #f0f9fd;
  color: #1ba6e2;
}

.dropdown-menu li.selected {
  color: #1ba6e2;
  font-weight: 600;
}

.dropdown-menu li.custom-option {
  border-top: 1px solid #f0f0f0;
  color: #1ba6e2;
}

/* 自定义日期面板 */
.custom-date-panel {
  position: absolute;
  top: 0;
  left: calc(100% + 8px);
  width: 260px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 101;
  padding: 16px;
}

.panel-header {
  font-size: 14px;
  color: #333;
  font-weight: 600;
  text-align: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 16px;
}

.panel-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.date-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.row-label {
  font-size: 13px;
  color: #666;
  width: 32px;
  flex-shrink: 0;
  text-align: right;
}

.date-input {
  flex: 1;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #d9e2f5;
  border-radius: 4px;
  font-size: 13px;
  color: #333;
  outline: none;
  cursor: pointer;
  box-sizing: border-box;
}

.date-input:focus {
  border-color: #1ba6e2;
}

.panel-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.panel-btn {
  padding: 6px 20px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s;
}

.panel-btn.confirm {
  background: transparent;
  color: #1ba6e2;
  border: none;
  font-weight: 600;
}

.panel-btn.confirm:hover {
  color: #168dbd;
}

.panel-btn.cancel {
  background: transparent;
  color: #666;
  border: none;
}

.panel-btn.cancel:hover {
  color: #333;
}

.param-value {
  color: #333;
}

.param-sep {
  color: #ddd;
}

/* 图例 */
.chart-legend {
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  padding-left: 8px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

/* 图表容器 */
.chart-container {
  width: 100%;
  height: 500px;
  position: relative;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
}

.chart-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  z-index: 5;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f0f0f0;
  border-top-color: #1ba6e2;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.chart-loading span {
  font-size: 14px;
  color: #999;
}

.chart-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
  color: #ff4d4f;
  z-index: 5;
}

.chart-empty,
.chart-no-data {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
  color: #bbb;
  z-index: 5;
}

/* 弹窗 */
.modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-dialog {
  width: 400px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.modal-header {
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.modal-body {
  padding: 24px;
}

.modal-body input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d9e2f5;
  border-radius: 20px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
}

.modal-body input:focus {
  border-color: #1ba6e2;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-cancel,
.btn-confirm {
  padding: 8px 24px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-cancel {
  background: #fff;
  color: #333;
  border: 1px solid #d9e2f5;
}

.btn-cancel:hover {
  border-color: #1ba6e2;
  color: #1ba6e2;
}

.btn-confirm {
  background: #1ba6e2;
  color: #fff;
  border: none;
}

.btn-confirm:hover {
  background: #168dbd;
}

/* ========== 登录弹窗遮罩层 ========== */
.login-modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-modal-container {
  position: relative;
  animation: modal-fade-in 0.3s ease;
}

@keyframes modal-fade-in {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>